import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { AlertTriangle, CheckCircle2, CircleDot, Eye } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { decisionRef } from '@/lib/decisionRef';
import { cn } from '@/lib/utils';
import { useLocalization } from '@/hooks/useLocalization';

interface Props { onNavigate: (view: string) => void }

type Col = 'needs' | 'review' | 'escalated' | 'approved';
interface Card { key: string; col: Col; title: string; meta: string; view: string; ref?: string }

const COLS: Array<{ key: Col; label: string; icon: typeof Eye; tone: string; empty: string }> = [
  { key: 'needs', label: 'Needs decision', icon: CircleDot, tone: 'text-[hsl(var(--warning-foreground))] bg-warning/20', empty: 'No decisions are waiting.' },
  { key: 'review', label: 'In review', icon: Eye, tone: 'text-primary bg-primary/10', empty: 'No AI outputs are in review.' },
  { key: 'escalated', label: 'Escalated', icon: AlertTriangle, tone: 'text-[hsl(var(--masira-priority-critical))] bg-[hsl(var(--masira-priority-critical))]/10', empty: 'Nothing escalated.' },
  { key: 'approved', label: 'Approved', icon: CheckCircle2, tone: 'text-foreground bg-success/20', empty: 'No approvals in the last 30 days.' },
];

/** Decision Intelligence: what needs a decision, and why. Real decisions + AI outputs awaiting review. */
const DecisionBoard = ({ onNavigate }: Props) => {
  const { tx, t, ago } = useLocalization();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['decision-board'],
    staleTime: 30_000,
    queryFn: async (): Promise<Card[]> => {
      const since = new Date(Date.now() - 30 * 86400_000).toISOString();
      const [dec, ai] = await Promise.all([
        supabase.from('decisions').select('id,title,status,project_name,due_date,decided_at,created_at,priority').order('created_at', { ascending: false }).limit(60),
        supabase.from('ai_outputs').select('id,title,summary,status,severity,created_at').in('status', ['pending_review', 'more_evidence_requested', 'escalated']).order('created_at', { ascending: false }).limit(30),
      ]);
      if (dec.error) throw new Error(dec.error.message);
      if (ai.error) throw new Error(ai.error.message);
      const cards: Card[] = [];
      for (const d of dec.data ?? []) {
        const ref = decisionRef(d.id, d.created_at);
        if (d.status === 'pending') {
          cards.push({ key: `d-${d.id}`, col: 'needs', title: d.title, ref, view: 'decisions',
            meta: [d.project_name, d.due_date ? `due ${format(new Date(d.due_date), 'd MMM')}` : null].filter(Boolean).join(' · ') || 'Decision pending' });
        } else if (d.status === 'approved' && d.decided_at && d.decided_at >= since) {
          cards.push({ key: `d-${d.id}`, col: 'approved', title: d.title, ref, view: 'decisions',
            meta: [d.project_name, `approved ${ago(d.decided_at)}`].filter(Boolean).join(' · ') });
        }
      }
      for (const a of ai.data ?? []) {
        cards.push({ key: `a-${a.id}`, col: a.status === 'escalated' ? 'escalated' : 'review', title: a.title, view: 'hitlWorkbench',
          meta: `${a.summary ?? 'AI output awaiting review'} · ${ago(a.created_at)}` });
      }
      return cards;
    },
  });

  if (isLoading) return <div className="grid gap-4 md:grid-cols-2"><Skeleton className="h-40" /><Skeleton className="h-40" /><Skeleton className="h-40" /><Skeleton className="h-40" /></div>;
  if (isError) return <p role="alert" className="text-sm text-muted-foreground">{t('Decisions could not be loaded. Try again shortly.')}</p>;

  const cards = data ?? [];
  const total = cards.filter((c) => c.col !== 'approved').length;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {total === 0 ? t('Nothing is waiting for a decision.') : t('{0} item{1} need attention. Every AI output stays a draft until a person approves it.', { 0: total, 1: total === 1 ? '' : 's' })}
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        {COLS.map((col) => {
          const items = cards.filter((c) => c.col === col.key);
          const Icon = col.icon;
          return (
            <section key={col.key} aria-label={tx(col.label)} className="rounded-lg border border-border bg-card p-4 shadow-sm">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-base font-semibold">
                <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[11px] uppercase tracking-wide', col.tone)}>
                  <Icon className="h-3.5 w-3.5" aria-hidden="true" /> {tx(col.label)}
                </span>
                <span className="ms-auto font-mono text-xs text-muted-foreground">{items.length}</span>
              </h2>
              {items.length === 0 ? (
                <p className="py-2 text-sm text-muted-foreground">{col.empty}</p>
              ) : (
                <ul className="space-y-2">
                  {items.slice(0, 5).map((c) => (
                    <li key={c.key}>
                      <button type="button" onClick={() => onNavigate(c.view)} className="w-full rounded-md border border-border px-3 py-2.5 text-start hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                        <span className="block text-sm font-semibold">{tx(c.title)}</span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">{c.meta}</span>
                        {c.ref && <span className="mt-1 block font-mono text-[11px] text-primary">{c.ref}</span>}
                      </button>
                    </li>
                  ))}
                  {items.length > 5 && <li className="px-1 text-xs text-muted-foreground">+ {items.length - 5}  {t('more in Decisions and Approvals')}</li>}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default DecisionBoard;
